// Copyright (c) StrangeBee 2023

import { ScopeContext } from '@Context/ScopeProvider';
import { classList } from '@Helpers/className';
import { useLocalMessage } from '@Helpers/locale';
import { formatTags } from '@Helpers/tags';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import THSelector from '@Molecules/THSelector/THSelector';
import DisplayTag from '@Organisms/DisplayTag/DisplayTag';
import { Space } from 'antd';
import React, { ReactElement, useContext, useState } from 'react';
import { TagOutput, TaxonomyOutput } from 'thehive-sdk';
import { ICONS } from '@Constants/iconsApp';
import { THTooltip } from '@Molecules/THTooltip/THTooltip';

import './TaxonomyTagSelect.scss';

interface TaxonomyTagSelectProps {
    onClick: (tags: TagOutput[]) => void;
    defaultTag?: TagOutput[];
    className?: string;
    triggerElem?: ReactElement;
}

const TaxonomyTagSelect = ({
    onClick,
    defaultTag = [],
    className = '',
    triggerElem,
}: TaxonomyTagSelectProps): ReactElement => {
    const i = useLocalMessage();

    const [actualTaxonomy, setActualTaxonomy] = useState<TaxonomyOutput>();
    const [newTags, setNewTags] = useState<TagOutput[]>(defaultTag);
    const {
        taxonomy: { list: taxonomyList = [], refetch: refetchTaxonomy },
    } = useContext(ScopeContext);

    const DisplayTags = (
        <div className="taxonomy-tag-select__tags">
            <div className="taxonomy-tag-select__title">
                <div className="taxonomy-tag-select__label">
                    {i('customTags.add.tags.title')}: ({newTags.length})
                </div>
                <div className="taxonomy-tag-select__button">
                    <THButton
                        type="link"
                        onClick={() => {
                            setNewTags([]);
                        }}
                    >
                        {i('customTags.add.tags.clear')}
                    </THButton>
                </div>
            </div>

            <div className="taxonomy-tag-select__tags-display">
                <DisplayTag.List<TagOutput> data={newTags}>
                    {(tag) => (
                        <DisplayTag
                            tag={tag}
                            onClick={(tag): void => setNewTags(newTags.filter((e) => formatTags(e) !== tag))}
                        />
                    )}
                </DisplayTag.List>
            </div>
        </div>
    );

    const ChooseTaxonomy = (
        <div>
            <div className="taxonomy-tag-select__title">
                <div className="taxonomy-tag-select__label">{i('customTags.add.taxonomy.choose-taxonomy')}:</div>
            </div>

            <THSelector<TaxonomyOutput>
                refKey="_id"
                dataSource={taxonomyList}
                onClick={(taxonomy): void => {
                    setActualTaxonomy(taxonomy);
                }}
            >
                {({ namespace, description }): ReactElement => (
                    <Space>
                        {namespace}{' '}
                        <THTooltip title={description}>
                            <ICONS.QuestionCircleFilled />
                        </THTooltip>
                    </Space>
                )}
            </THSelector>
        </div>
    );

    const ChooseTag = (
        <div>
            <div className="taxonomy-tag-select__title">
                <div className="taxonomy-tag-select__label">
                    {i('customTags.add.taxonomy.choose-tags')}: {actualTaxonomy?.namespace}
                </div>
                <div className="taxonomy-tag-select__button">
                    <THButton type="link" onClick={(): void => setActualTaxonomy(undefined)}>
                        {i('customTags.add.taxonomy.another')}
                    </THButton>
                </div>
            </div>

            {actualTaxonomy && (
                <THSelector<TagOutput>
                    refKey="_id"
                    dataSource={actualTaxonomy.tags}
                    className="taxonomy-tag-select__list"
                    value={newTags}
                    customSelect={(tag, value) => value.includes(tag)}
                    onChange={(callback): void => setNewTags(callback)}
                    filtering={{
                        search: ({ description, ...tag }): string => `${formatTags(tag)} ${description}`,
                        placeholder: i('customTags.add.taxonomy.filter'),
                    }}
                >
                    {(tag, selected): ReactElement => (
                        <Space className="taxonomy-tag-select__wrap-item">
                            {selected ? <ICONS.CheckCircleTwoTone /> : <ICONS.CheckCircleOutlined />}
                            <DisplayTag tag={tag} ellipsis={false} className="taxonomy-tag-select__item" />
                            <THTooltip title={tag.description}>
                                <ICONS.QuestionCircleFilled />
                            </THTooltip>
                        </Space>
                    )}
                </THSelector>
            )}
        </div>
    );

    return (
        <THDrawer
            title={i('customTags.add.title')}
            triggerElem={
                triggerElem || (
                    <THButton type="primary" className={classList('drawer-taxonomy__btn', className)}>
                        +
                    </THButton>
                )
            }
            footer={(toggleDrawer): ReactElement => (
                <THButton
                    block
                    type="primary"
                    onClick={(): void => {
                        toggleDrawer();
                        onClick(newTags);
                        setNewTags([]);
                        setActualTaxonomy(undefined);
                    }}
                    disabled={!newTags.length}
                >
                    {i('customTags.add.footer')}
                </THButton>
            )}
            onClose={(): void => {
                setNewTags([]);
                setActualTaxonomy(undefined);
            }}
            afterVisibleChange={(visible) => visible && refetchTaxonomy?.()}
        >
            <div className="taxonomy-tag-select">
                {DisplayTags}
                {actualTaxonomy ? ChooseTag : ChooseTaxonomy}
            </div>
        </THDrawer>
    );
};

export default TaxonomyTagSelect;
